/**
 * CustomSelect - Custom dropdown component to replace native select
 * This allows full control over styling, including removing borders from the popup
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CustomSelectProps {
  value: string;
  options: string[] | Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  title?: string;
  style?: React.CSSProperties;
  className?: string;
  inputRef?: (el: HTMLInputElement | null) => void;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  options,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  disabled = false,
  title,
  style,
  className = '',
  inputRef
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRefInternal = useRef<HTMLInputElement>(null);

  const isObjectArray = options.length > 0 && typeof options[0] !== 'string';
  const optionsList = isObjectArray 
    ? (options as Array<{ value: string; label: string }>)
    : (options as string[]).map(opt => ({ value: opt, label: opt }));

  // Try exact match first
  let selectedOption = optionsList.find(opt => opt.value === value);
  
  // If no exact match, try case-insensitive match
  if (!selectedOption && value) {
    selectedOption = optionsList.find(opt => 
      opt.value.toLowerCase() === value.toLowerCase() || 
      opt.label.toLowerCase() === value.toLowerCase()
    );
  }
  
  // If still no match, show the actual value instead of '-'
  const displayValue = selectedOption ? selectedOption.label : (value || '-');

  // Calculate dropdown position when opened and update on scroll
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const dropdownHeight = 200; // maxHeight
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          
          // If not enough space below but enough space above, flip upward
          const shouldFlipUp = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
          
          setDropdownPosition({
            top: shouldFlipUp ? rect.top - dropdownHeight - 2 : rect.bottom + 2,
            left: rect.left,
            width: rect.width
          });
        }
      };

      // Initial position
      updatePosition();

      // Update position on scroll (capture phase to catch all scroll events)
      const scrollableParents = containerRef.current ? 
        Array.from(document.querySelectorAll('.universal-table-container')) : [];
      
      scrollableParents.forEach(parent => {
        parent.addEventListener('scroll', updatePosition, true);
      });
      
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        scrollableParents.forEach(parent => {
          parent.removeEventListener('scroll', updatePosition, true);
        });
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && 
        !containerRef.current.contains(target) &&
        !(target instanceof Element && target.closest('.custom-select-dropdown'))
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
        if (onBlur) onBlur();
      }
    };

    if (isOpen) {
      // Use a small delay to avoid closing immediately when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onBlur]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen && focusedIndex >= 0) {
        handleSelect(optionsList[focusedIndex].value);
      } else {
        setIsOpen(!isOpen);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(-1);
      if (onBlur) onBlur();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setFocusedIndex(prev => (prev < optionsList.length - 1 ? prev + 1 : prev));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
      }
    } else if (e.key === 'Tab') {
      setIsOpen(false);
      setFocusedIndex(-1);
    } else {
      // Pass the event to the parent handler, casting to match expected type
      if (onKeyDown) onKeyDown(e as any);
    }
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setFocusedIndex(-1);
    if (onBlur) onBlur();
  };

  const handleFocus = () => {
    if (onFocus) onFocus();
  };

  const handleClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && onFocus) onFocus();
    }
  };

  // Combine refs - pass the div as inputRef for table navigation
  useEffect(() => {
    if (inputRef && inputRefInternal.current) {
      // Pass the div element as the ref (table navigation expects HTMLInputElement but will work with any HTMLElement)
      inputRef(inputRefInternal.current as any);
    }
  }, [inputRef]);

  // Force normal font weight using setProperty to override any CSS rules
  useEffect(() => {
    if (inputRefInternal.current) {
      inputRefInternal.current.style.setProperty('font-weight', '400', 'important');
      // Also force font-weight on the span inside
      const span = inputRefInternal.current.querySelector('span');
      if (span) {
        span.style.setProperty('font-weight', '400', 'important');
      }
    }
  }, [value, disabled]);

  // Force normal font weight on dropdown options
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const options = containerRef.current.querySelectorAll('.custom-select-option');
      options.forEach((option) => {
        (option as HTMLElement).style.setProperty('font-weight', '400', 'important');
      });
    }
  }, [isOpen, value]);

  return (
    <div 
      ref={containerRef}
      className={`custom-select-container ${className}`}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <div
        ref={inputRefInternal as any}
        className="custom-select-input"
        style={{
          ...style,
          width: '100%',
          height: '100%',
          padding: style?.padding || '4px 8px',
          border: 'none',
          borderWidth: 0,
          borderStyle: 'none',
          borderColor: 'transparent',
          outline: 'none',
          boxShadow: 'none',
          background: style?.background || (value ? '#fff' : '#f9fafb'),
          color: style?.color || '#000000',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: style?.fontSize || '12px',
          fontWeight: style?.fontWeight || 400,
          textAlign: style?.textAlign || 'center',
          fontFamily: style?.fontFamily || 'inherit',
          boxSizing: 'border-box',
          opacity: disabled ? 0.6 : 1,
          userSelect: 'none' as const
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        tabIndex={disabled ? -1 : 0}
        title={title}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled}
      >
        <span style={{ flex: 1, textAlign: style?.textAlign || 'center', fontWeight: 400 }}>
          {displayValue}
        </span>
        <span style={{ 
          marginLeft: '4px', 
          fontSize: '10px',
          opacity: 0.6,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}>
          ▼
        </span>
      </div>

      {isOpen && !disabled && dropdownPosition && createPortal(
        <div
          className="custom-select-dropdown"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 99999,
            backgroundColor: '#ffffff',
            border: 'none',
            borderWidth: 0,
            borderStyle: 'none',
            borderColor: 'transparent',
            outline: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '4px',
            marginTop: '2px',
            maxHeight: '200px',
            overflowY: 'auto',
            padding: 0,
            pointerEvents: 'auto'
          }}
          role="listbox"
          onClick={(e) => e.stopPropagation()}
        >
          {optionsList.map((option, index) => (
            <div
              key={option.value}
              className="custom-select-option"
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: index === focusedIndex || option.value === value 
                  ? '#e6f4fb' 
                  : '#ffffff',
                color: '#000000',
                border: 'none',
                borderWidth: 0,
                borderStyle: 'none',
                borderColor: 'transparent',
                outline: 'none',
                fontSize: style?.fontSize || '12px',
                fontWeight: 400,
                textAlign: style?.textAlign || 'center'
              }}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setFocusedIndex(index)}
              role="option"
              aria-selected={option.value === value}
            >
              {option.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

